/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AuthTextField.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Narender - narender@au79consulting.com
 * @date 12-06-2024
 * @description Reusable text field component for authentication-related inputs.
 */

import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import appMeta from "../../assets/app-meta/appMeta";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { componentStyle } from "../../config/appTheme";

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 12-06-2024
 * @description Reusable text field component for authentication-related inputs.
 * @param label, placeHolder, type, value, setValue, valueError, setValueError, isAutoFocused, isRequired
 * @return The rendered text field component.
 */
function AuthTextField({
  label,
  placeHolder,
  type,
  fldType,
  variant,
  required = true,
  inputProps,
  passwordValue = "",
  value,
  setValue,
  valueError,
  setValueError,
  isAutoFocused,
  isRequired,
  txtFldStyle,
  onKeyDown,
  minLength,
  maxLength,
  countUnit = "character",
}) {
  const resolvedMinLength = useMemo(() => {
    if (typeof minLength === "number") return minLength;
    if (fldType === appMeta.textFieldTypes.FULL_NAME) return 3;
    return null;
  }, [minLength, fldType]);

  const resolvedMaxLength = useMemo(() => {
    if (typeof maxLength === "number") return maxLength;
    if (fldType === appMeta.textFieldTypes.FULL_NAME) return 35;
    return null;
  }, [maxLength, fldType]);

  const mergedInputProps = {
    ...inputProps,
    sx: {
      backgroundColor: "#fff",
      height: "32px",
      pr: 0,

      "& .MuiInputBase-input": {
        pr: 0,
      },

      "& .MuiInputAdornment-root": {
        m: 0,
        p: 0,
        height: "100%",
      },
    },
  };
  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d9d9d9",
        borderRadius: "0px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#8c8c8c",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#000000",
      },
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e0e0e0",
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e53935",
      },
      "& .MuiInputBase-input": {
        ...componentStyle.font.form.textFieldValue,
      },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      ...componentStyle.font.form.textFieldValue,
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },

    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
      ...componentStyle.font.form.textFieldValue,
    },

    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      ...componentStyle.font.form.textFieldError,
      marginLeft: 0,
      marginTop: "4px",
    },

    ...txtFldStyle,
  };
  const [showPassword, setShowPassword] = useState(false);
  const [textCountValidation, setTextCountValidation] = useState("");
  const [maxCharacterValidation, setMaxCharacterValidation] = useState("");
  const [length, setLength] = useState(0);

  // Toggles the visibility of the password input.
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateMinLength = (trimmedLength) => {
    if (!resolvedMinLength) return setTextCountValidation("");

    setTextCountValidation(
      trimmedLength > 0 && trimmedLength < resolvedMinLength
        ? String(resolvedMinLength)
        : "",
    );
  };

  const validateMaxLength = (newValue) => {
    if (!resolvedMaxLength) {
      setMaxCharacterValidation("");
      return false;
    }

    setMaxCharacterValidation(
      newValue.length >= resolvedMaxLength ? String(resolvedMaxLength) : "",
    );

    return newValue.length > resolvedMaxLength;
  };

  const handleEmail = (newValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    setValueError(emailRegex.test(newValue) ? "" : "Invalid email address");
    setValue(newValue);
  };

  const handlePassword = (newValue) => {
    const isValid =
      newValue.length >= 8 &&
      /[a-z]/.test(newValue) &&
      /[A-Z]/.test(newValue) &&
      /[0-9]/.test(newValue) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(newValue);

    setValueError(isValid ? "" : "Password is weak");
    setValue(newValue);
  };

  const handleAuthPassword = (newValue) => {
    if (newValue.length >= 8) setValueError("");
    setValue(newValue);
  };

  const handleConfirmPassword = (newValue) => {
    setValueError(newValue !== passwordValue ? "Passwords do not match." : "");
    setValue(newValue);
  };

  const handleOTP = (newValue) => {
    const otpRegex = /^\d{0,6}$/;

    if (otpRegex.test(newValue)) {
      setValue(newValue);
      setValueError("");
    } else if (newValue.length <= 6) {
      setValueError("Code must be a numeric value");
    }
  };

  const handleFullName = (newValue) => {
    if (newValue?.trim() === "") {
      setValueError("Full Name is required");
      return true; // stop further execution
    }

    setValueError("");
    setValue(newValue);
    return false;
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    const trimmedLength = newValue.trim().length;

    setLength(trimmedLength);

    validateMinLength(trimmedLength);

    const isExceeded = validateMaxLength(newValue);
    if (isExceeded) return;

    switch (fldType) {
      case appMeta.textFieldTypes.EMAIL:
        handleEmail(newValue);
        break;

      case appMeta.textFieldTypes.PASSWORD:
        handlePassword(newValue);
        break;

      case appMeta.textFieldTypes.AUTHPASSWORD:
        handleAuthPassword(newValue);
        break;

      case appMeta.textFieldTypes.CONFIRM_PASSWORD:
        handleConfirmPassword(newValue);
        break;

      case appMeta.textFieldTypes.OTP:
        handleOTP(newValue);
        break;

      case appMeta.textFieldTypes.FULL_NAME:
        if (handleFullName(newValue)) return;
        break;

      default:
        break;
    }
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            // mb: 0.5,
            color: "#6f7174",
            ...componentStyle.font.form.textFieldLabel,
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}

      {Boolean(textCountValidation) && (
        <Box sx={{ textAlign: "left" }}>
          <Typography
            sx={{
              mb: 0.5,
              ...componentStyle.font.form.textFieldError,
              fontWeight: 500,
              color: "#6f7174",
            }}
          >
            Minimum{" "}
            <Typography
              component="span"
              sx={{
                color: "red",
                fontSize: "0.8rem",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {textCountValidation}
            </Typography>{" "}
            {countUnit + (Number(textCountValidation) > 1 ? "s" : "")} required
          </Typography>
        </Box>
      )}

      {Boolean(maxCharacterValidation) && (
        <Box sx={{ textAlign: "left" }}>
          <Typography
            sx={{
              mb: 0.5,
              ...componentStyle.font.form.textFieldError,
              fontWeight: 500,
              color: "#6f7174",
            }}
          >
            Maximum{" "}
            <Typography
              component="span"
              sx={{
                color: "red",
                fontSize: "0.8rem",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {maxCharacterValidation}
            </Typography>{" "}
            {countUnit + (Number(maxCharacterValidation) > 1 ? "s" : "")}{" "}
            allowed
          </Typography>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        type={type === "password" ? (showPassword ? "text" : "password") : type}
        variant="outlined"
        placeholder={placeHolder}
        value={value}
        onChange={handleChange}
        required={isRequired}
        error={Boolean(valueError)}
        autoFocus={isAutoFocused}
        onKeyDown={onKeyDown}
        inputProps={{
          ...inputProps,
          ...(resolvedMaxLength ? { maxLength: resolvedMaxLength } : {}),
        }}
        sx={textFieldStyle}
        InputProps={
          fldType === appMeta.textFieldTypes.PASSWORD ||
            fldType === appMeta.textFieldTypes.AUTHPASSWORD ||
            fldType === appMeta.textFieldTypes.CONFIRM_PASSWORD
            ? {
              ...mergedInputProps,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityIcon />
                    ) : (
                      <VisibilityOffIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }
            : mergedInputProps
        }
      />

      {Boolean(valueError) && (
        <Box sx={{ textAlign: "left" }}>
          <Typography
            sx={{
              mb: 0.5,
              ...componentStyle.font.form.textFieldError,
              fontWeight: 500,
              color: "#e53935",
            }}
          >
            {valueError}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default AuthTextField;
